import { WALKING_SPEED_KMH } from "./constants";
import type { Geopoint } from "./types";

const EARTH_RADIUS_KM = 6371;

function toRadians(deg: number): number {
  return (deg * Math.PI) / 180;
}

/**
 * Great-circle distance between two coordinates, in kilometres.
 * Adequate for the V&A's compact, walkable footprint — the precinct
 * is small enough that the straight-line vs. street-network gap is
 * dominated by other UX rounding.
 */
export function haversineKm(a: Geopoint, b: Geopoint): number {
  const dLat = toRadians(b.lat - a.lat);
  const dLng = toRadians(b.lng - a.lng);
  const lat1 = toRadians(a.lat);
  const lat2 = toRadians(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(h));
}

export function haversineMetres(a: Geopoint, b: Geopoint): number {
  return haversineKm(a, b) * 1000;
}

export function walkMinutes(km: number, speedKmh = WALKING_SPEED_KMH): number {
  return (km / speedKmh) * 60;
}

/** Convert "HH:MM" to minutes since midnight. */
export function timeToMinutes(time: string): number {
  const [h = 0, m = 0] = time.split(":").map(Number);
  return h * 60 + m;
}

/** Convert minutes since midnight to "HH:MM". */
export function minutesToTime(minutes: number): string {
  const wrapped = ((minutes % (24 * 60)) + 24 * 60) % (24 * 60);
  const h = Math.floor(wrapped / 60);
  const m = Math.floor(wrapped % 60);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}
